import { useState, useMemo } from 'react';
import { Save, ChevronDown, ChevronUp } from 'lucide-react';
import Swal from 'sweetalert2';

// ─────────────────────────────────────────────────────────────────────────────
// DATA DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

const TOTAL_MARLA = 80;
const MARLA_COLS = Array.from({ length: TOTAL_MARLA }, (_, i) => ({
  key: String(i + 1),
  label: (i + 1) + ' Marla',
}));

const SHEET1_ROWS = [
  { key: 'basement', label: 'Basement Floor Covered Area' },
  { key: 'ground',   label: 'Ground Floor Covered Area'   },
  { key: 'first',    label: 'First Floor Covered Area'    },
  { key: 'second',   label: 'Second Floor Covered Area'   },
];

const SHEET2_FIELDS = [
  { key: 'basement_grey_budget',    label: 'Basement Grey Structure Budget / Sq Ft'      },
  { key: 'grey_budget_house_other', label: 'Grey Structure Budget House Other / Sq Ft'   },
  { key: 'basement_grey_good',      label: 'Basement Grey Structure Good / Sq Ft'        },
  { key: 'grey_good_house_other',   label: 'Grey Structure Good House Other / Sq Ft'     },
  { key: 'budget_finishes',         label: 'Budget Finishes / Sq Ft'                     },
  { key: 'a_quality_finishes',      label: 'A Quality Finishes / Sq Ft'                  },
  { key: 'premium_quality',         label: 'Premium Quality Finishes / Sq Ft'            },
];

const FLOOR_LABEL = {
  basement: 'Basement Floor',
  ground:   'Ground Floor',
  first:    'First Floor',
  second:   'Second Floor',
};

// Quality config — grey structure + finish rates per quality type
// (Dummy formulas — replace with client formulas when received)
const QUALITY_CFG = {
  budget: {
    label:        'Budget',
    greyBasement: 'basement_grey_budget',
    greyOther:    'grey_budget_house_other',
    finish:       'budget_finishes',
    badgeBg:      '#dbeafe',
    badgeColor:   '#1e40af',
  },
  aQuality: {
    label:        'A Quality',
    greyBasement: 'basement_grey_good',
    greyOther:    'grey_good_house_other',
    finish:       'a_quality_finishes',
    badgeBg:      '#dcfce7',
    badgeColor:   '#14532d',
  },
  premium: {
    label:        'Premium',
    greyBasement: 'basement_grey_good',
    greyOther:    'grey_good_house_other',
    finish:       'premium_quality',
    badgeBg:      '#fef3c7',
    badgeColor:   '#92400e',
  },
};

// 20 generated tables — exact from Excel sheet
const TABLE_CONFIGS = [
  // ── Single Storey
  { id: 1,  name: 'Single Storey Budget House',                              quality: 'budget',    floors: ['ground'] },
  { id: 2,  name: 'Single Storey A Quality House',                           quality: 'aQuality',  floors: ['ground'] },
  { id: 3,  name: 'Single Storey Premium Quality House',                     quality: 'premium',   floors: ['ground'] },
  // ── Double Storey
  { id: 4,  name: 'Double Storey Budget House',                              quality: 'budget',    floors: ['ground','first'] },
  { id: 5,  name: 'Double Storey A Quality House',                           quality: 'aQuality',  floors: ['ground','first'] },
  { id: 6,  name: 'Double Storey Premium Quality House',                     quality: 'premium',   floors: ['ground','first'] },
  // ── Basement + Double Storey
  { id: 7,  name: 'Basement Double Storey Budget House',                     quality: 'budget',    floors: ['basement','ground','first'] },
  { id: 8,  name: 'Basement Double Storey A Quality House',                  quality: 'aQuality',  floors: ['basement','ground','first'] },
  { id: 9,  name: 'Basement Double Storey Premium Quality House',            quality: 'premium',   floors: ['basement','ground','first'] },
  // ── Basement + Triple Storey
  { id: 10, name: 'Basement Triple Storey Budget House',                     quality: 'budget',    floors: ['basement','ground','first','second'] },
  { id: 11, name: 'Basement Triple Storey A Quality House',                  quality: 'aQuality',  floors: ['basement','ground','first','second'] },
  { id: 12, name: 'Basement Triple Storey Premium Quality House',            quality: 'premium',   floors: ['basement','ground','first','second'] },
  // ── Basement + Triple Storey + Mezzanine
  { id: 13, name: 'Basement Triple Storey Budget House with Mezzanine',      quality: 'budget',    floors: ['basement','ground','first','second'] },
  { id: 14, name: 'Basement Triple Storey A Quality House with Mezzanine',   quality: 'aQuality',  floors: ['basement','ground','first','second'] },
  { id: 15, name: 'Basement Triple Storey Premium House with Mezzanine',     quality: 'premium',   floors: ['basement','ground','first','second'] },
  // ── Without Basement + Mezzanine
  { id: 16, name: 'Budget House with Mezzanine',                             quality: 'budget',    floors: ['ground','first'] },
  { id: 17, name: 'A Quality House with Mezzanine',                          quality: 'aQuality',  floors: ['ground','first','second'] },
  { id: 18, name: 'Premium Quality House with Mezzanine',                    quality: 'premium',   floors: ['ground','first','second'] },
  // ── Extended
  { id: 19, name: 'A Quality House with Mezzanine (Extended)',               quality: 'aQuality',  floors: ['ground','first','second'] },
  { id: 20, name: 'Premium Quality House with Mezzanine (Extended)',         quality: 'premium',   floors: ['basement','ground','first','second'] },
];

// Reference table floor-config columns
const REF_COLS = [
  { label: 'Ground Only',                      floors: ['ground'] },
  { label: 'Ground + First',                   floors: ['ground','first'] },
  { label: 'Basement + Ground',                floors: ['basement','ground'] },
  { label: 'Basement + Ground + First',        floors: ['basement','ground','first'] },
  { label: 'Basement + Ground + First + Second', floors: ['basement','ground','first','second'] },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const safeN = (v) => (v === '' || v == null ? 0 : Number(v));
const fmt   = (v) => v ? Number(v).toLocaleString('en-PK', { maximumFractionDigits: 0 }) : '—';
const fmtRs = (v) => v ? 'Rs ' + Number(v).toLocaleString('en-PK', { maximumFractionDigits: 0 }) : '—';

const buildSheet1 = () => {
  const g = {};
  SHEET1_ROWS.forEach(({ key }) => {
    g[key] = {};
    MARLA_COLS.forEach(({ key: c }) => { g[key][c] = ''; });
  });
  return g;
};
const buildSheet2 = () => {
  const o = {};
  SHEET2_FIELDS.forEach(({ key }) => { o[key] = ''; });
  return o;
};

// DUMMY formula — compute grey + finish per floor per table config
const computeTable = (config, marlaKey, sheet1, sheet2) => {
  const qc = QUALITY_CFG[config.quality];
  const rows = config.floors.map((f) => {
    const area      = safeN(sheet1[f]?.[marlaKey]);
    const greyRate  = f === 'basement' ? safeN(sheet2[qc.greyBasement]) : safeN(sheet2[qc.greyOther]);
    const finRate   = safeN(sheet2[qc.finish]);
    return { label: FLOOR_LABEL[f], area, grey: area * greyRate, finish: area * finRate };
  });
  return {
    rows,
    totArea:   rows.reduce((s, r) => s + r.area,   0),
    totGrey:   rows.reduce((s, r) => s + r.grey,   0),
    totFinish: rows.reduce((s, r) => s + r.finish, 0),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 1 GRID
// ─────────────────────────────────────────────────────────────────────────────
const CELL_W  = 96;
const LABEL_W = 235;
const kd = (e) => { if (['-','+','e','E'].includes(e.key)) e.preventDefault(); };

const Sheet1Grid = ({ data, onChange }) => (
  <div className='ui-card overflow-hidden p-0'>
    <div className='flex items-center gap-3 border-b border-[#15173d]/[0.06] px-5 py-4'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#982598]/10'>
        <span className='text-[11px] font-bold text-[#982598]'>1</span>
      </div>
      <div className='flex-1'>
        <h3 className='text-[11px] font-semibold uppercase tracking-[0.18em] text-[#982598]'>Input Sheet 1</h3>
        <p className='text-[11px] text-gray-400'>Floor covered areas (sq ft) — different value per marla. Scroll right to see all 80 marla columns.</p>
      </div>
      <span className='hidden sm:block rounded-full bg-[#f5eeff] px-2.5 py-1 text-[10px] font-semibold text-[#982598]'>4 rows × 80 cols</span>
    </div>
    <div style={{ overflowX:'auto' }}>
      <table style={{ borderCollapse:'separate', borderSpacing:0, minWidth:(LABEL_W + TOTAL_MARLA*CELL_W)+'px' }}>
        <thead>
          <tr>
            <th style={{ position:'sticky',left:0,zIndex:20,background:'linear-gradient(135deg,#15173d,#2d1050)',minWidth:LABEL_W,width:LABEL_W,padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(255,255,255,0.7)',borderRight:'1px solid rgba(255,255,255,0.1)' }}>
              Floor
            </th>
            {MARLA_COLS.map(({ key, label }) => (
              <th key={key} style={{ background:'linear-gradient(135deg,#15173d,#2d1050)',minWidth:CELL_W,width:CELL_W,padding:'10px 4px',textAlign:'center',fontSize:10,fontWeight:600,whiteSpace:'nowrap',color:Number(key)%5===0?'#f0c6ff':'rgba(255,255,255,0.55)',borderRight:Number(key)%5===0?'1px solid rgba(152,37,152,0.4)':'1px solid rgba(255,255,255,0.05)' }}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SHEET1_ROWS.map(({ key: rk, label }, ri) => {
            const bg = ri%2===0?'#fff':'#faf9ff';
            return (
              <tr key={rk}>
                <td style={{ position:'sticky',left:0,zIndex:10,background:bg,minWidth:LABEL_W,width:LABEL_W,padding:'7px 14px',fontSize:11,fontWeight:700,color:'#15173d',borderRight:'2px solid #f0e4f0',borderBottom:'1px solid #f3f0f8',whiteSpace:'nowrap' }}>{label}</td>
                {MARLA_COLS.map(({ key: col }) => (
                  <td key={col} style={{ background:bg,padding:'3px',borderRight:Number(col)%5===0?'1px solid #f0e4f0':'1px solid #f5f3fa',borderBottom:'1px solid #f3f0f8' }}>
                    <input type='number' min='0' step='any' dir='ltr' placeholder='0'
                      value={data[rk]?.[col] ?? ''}
                      onChange={(e) => { if(e.target.value!==''&&Number(e.target.value)<0) return; onChange(rk, col, e.target.value); }}
                      onKeyDown={kd}
                      onFocus={(e)=>{e.target.style.background='#f5f0ff';e.target.style.outline='1.5px solid #982598';e.target.style.borderRadius='4px';}}
                      onBlur={(e)=>{e.target.style.background='transparent';e.target.style.outline='none';}}
                      style={{ width:'100%',border:'none',outline:'none',background:'transparent',fontSize:12,textAlign:'center',color:'#15173d',fontWeight:500,padding:'4px 2px',borderRadius:4 }}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 2 FORM (simple — no marla columns)
// ─────────────────────────────────────────────────────────────────────────────
const Sheet2Form = ({ data, onChange }) => (
  <div className='ui-card'>
    <div className='mb-5 flex items-center gap-3'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#982598]/10'>
        <span className='text-[11px] font-bold text-[#982598]'>2</span>
      </div>
      <div>
        <h3 className='text-[11px] font-semibold uppercase tracking-[0.18em] text-[#982598]'>Input Sheet 2</h3>
        <p className='text-[11px] text-gray-400'>Construction rates per sq ft (PKR) — one value each, same for all marla sizes</p>
      </div>
    </div>
    <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
      {SHEET2_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <label className='ui-form-label'>{label}</label>
          <div className='relative'>
            <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:11,fontWeight:700,color:'#982598',pointerEvents:'none' }}>PKR</span>
            <input type='number' min='0' step='any' dir='ltr' placeholder='0'
              value={data[key] ?? ''}
              onChange={(e) => { if(e.target.value!==''&&Number(e.target.value)<0) return; onChange(key,e.target.value); }}
              onKeyDown={kd}
              className='ui-input-field'
              style={{ paddingLeft:44 }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// REFERENCE AREA TABLE (top of preview — mirrors Excel top table)
// ─────────────────────────────────────────────────────────────────────────────
const ReferenceTable = ({ marlaKey, sheet1 }) => {
  const areas = {};
  SHEET1_ROWS.forEach(({ key }) => { areas[key] = safeN(sheet1[key]?.[marlaKey]); });

  return (
    <div className='ui-card overflow-hidden p-0'>
      <div className='border-b border-[#15173d]/[0.06] bg-gradient-to-r from-[#15173d] to-[#2d1050] px-5 py-3.5'>
        <p className='text-[11px] font-bold uppercase tracking-[0.18em] text-white/70'>
          Square Feet Area of House — {marlaKey} Marla
        </p>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', minWidth:650 }}>
          <thead>
            <tr>
              <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',whiteSpace:'nowrap' }}>Floor</th>
              {REF_COLS.map(({ label }) => (
                <th key={label} style={{ background:'#f8f5ff',padding:'9px 12px',textAlign:'center',fontSize:10,fontWeight:700,color:'#982598',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SHEET1_ROWS.map(({ key, label }, ri) => (
              <tr key={key} style={{ background: ri%2===0?'#fff':'#faf9ff' }}>
                <td style={{ padding:'8px 14px',fontSize:12,fontWeight:600,color:'#15173d',borderBottom:'1px solid #f5f3fa' }}>{label}</td>
                {REF_COLS.map(({ label: cl, floors }) => {
                  const val = floors.includes(key) ? areas[key] : null;
                  return (
                    <td key={cl} style={{ padding:'8px 12px',textAlign:'center',fontSize:12,fontWeight:val?700:400,color:val?'#15173d':'#d1d5db',borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>
                      {val ? fmt(val) : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ background:'linear-gradient(135deg,#15173d,#2d1050)' }}>
              <td style={{ padding:'9px 14px',fontSize:12,fontWeight:800,color:'#fff' }}>Total</td>
              {REF_COLS.map(({ label: cl, floors }) => {
                const total = floors.reduce((s,f) => s + (areas[f]||0), 0);
                return (
                  <td key={cl} style={{ padding:'9px 12px',textAlign:'center',fontSize:12,fontWeight:800,color:'#f0c6ff',borderLeft:'1px solid rgba(255,255,255,0.1)' }}>
                    {fmt(total)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE GENERATED TABLE (collapsible)
// ─────────────────────────────────────────────────────────────────────────────
const GeneratedTable = ({ config, marlaKey, sheet1, sheet2 }) => {
  const [open, setOpen] = useState(config.id <= 3);
  const qc = QUALITY_CFG[config.quality];

  const { rows, totArea, totGrey, totFinish } = useMemo(
    () => computeTable(config, marlaKey, sheet1, sheet2),
    [config, marlaKey, sheet1, sheet2]
  );
  const hasData = rows.some(r => r.area > 0);

  return (
    <div className='overflow-hidden rounded-2xl border border-[#15173d]/[0.07] bg-white shadow-[0_2px_10px_rgba(21,23,61,0.04)]'>
      {/* Header */}
      <button type='button' onClick={() => setOpen(o => !o)}
        className='flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50/70 transition-colors'>
        <div className='flex items-center gap-2 flex-1 min-w-0 flex-wrap'>
          <span style={{ fontSize:10,fontWeight:700,background:'linear-gradient(135deg,#982598,#2d1050)',color:'#fff',borderRadius:5,padding:'2px 8px',whiteSpace:'nowrap',flexShrink:0 }}>
            #{config.id}
          </span>
          <span style={{ fontSize:10,fontWeight:700,background:qc.badgeBg,color:qc.badgeColor,borderRadius:5,padding:'2px 8px',whiteSpace:'nowrap',flexShrink:0 }}>
            {qc.label}
          </span>
          <span className='text-[13px] font-bold text-[#15173d] truncate'>{config.name}</span>
          {!hasData && (
            <span className='rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-600 shrink-0'>No data entered</span>
          )}
        </div>
        <div className='flex items-center gap-3 ml-3 shrink-0'>
          {hasData && (
            <span className='hidden md:block text-[11px] font-semibold text-gray-400'>
              Total: {fmtRs(totGrey + totFinish)}
            </span>
          )}
          {open ? <ChevronUp size={15} className='text-gray-400'/> : <ChevronDown size={15} className='text-gray-400'/>}
        </div>
      </button>

      {/* Table body */}
      {open && (
        <div style={{ overflowX:'auto', borderTop:'1px solid #f3f0f8' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', minWidth:480 }}>
            <thead>
              <tr>
                <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',whiteSpace:'nowrap' }}>Floor</th>
                <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Area (sq ft)</th>
                <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:qc.badgeColor,borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Grey Structure</th>
                <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:qc.badgeColor,borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Finishes</th>
                <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={row.label} style={{ background:ri%2===0?'#fff':'#faf9ff' }}>
                  <td style={{ padding:'9px 14px',fontSize:12,fontWeight:600,color:'#374151',borderBottom:'1px solid #f5f3fa' }}>{row.label}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmt(row.area)}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#374151',fontWeight:500,borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmtRs(row.grey)}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#374151',fontWeight:500,borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmtRs(row.finish)}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#15173d',fontWeight:700,borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmtRs(row.grey+row.finish)}</td>
                </tr>
              ))}
              {/* Total row */}
              <tr style={{ background:'linear-gradient(135deg,#15173d,#2d1050)' }}>
                <td style={{ padding:'9px 14px',fontSize:12,fontWeight:800,color:'#fff' }}>Total</td>
                <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'rgba(255,255,255,0.7)',fontWeight:600,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmt(totArea)}</td>
                <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#c4b5fd',fontWeight:800,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmtRs(totGrey)}</td>
                <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#c4b5fd',fontWeight:800,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmtRs(totFinish)}</td>
                <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#f0c6ff',fontWeight:800,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmtRs(totGrey+totFinish)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const CostOfConstruction = () => {
  const [sheet1, setSheet1]           = useState(buildSheet1);
  const [sheet2, setSheet2]           = useState(buildSheet2);
  const [previewMarla, setPreviewMarla] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSheet1Change = (rk, col, val) =>
    setSheet1(prev => ({ ...prev, [rk]: { ...prev[rk], [col]: val } }));
  const handleSheet2Change = (key, val) =>
    setSheet2(prev => ({ ...prev, [key]: val }));
  const handleCancel = () => { setSheet1(buildSheet1()); setSheet2(buildSheet2()); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // TODO: replace with real API call — payload: { sheet1, sheet2 }
      await new Promise(r => setTimeout(r, 800));
      Swal.fire({ icon:'success', title:'Saved!', text:'Cost of construction data saved successfully.', confirmButtonText:'OK' });
    } catch (err) {
      Swal.fire({ icon:'error', title:'Error', text: err?.message||'Failed to save.' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-5 pb-8'>

      {/* ── Page Header */}
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h2 className='text-xl font-extrabold text-[#15173d]'>Cost of Construction</h2>
          <p className='text-xs text-gray-500'>
            Update input values — 20 tables auto-calculate (used in 140 blogs)
          </p>
        </div>
        <div className='flex gap-2'>
          <button type='button' className='ui-btn-cancel' onClick={handleCancel} disabled={isSubmitting}>Reset</button>
          <button type='submit' className='ui-btn-save' disabled={isSubmitting}>
            <Save size={15} />
            {isSubmitting ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      {/* ── Info bar */}
      <div className='flex items-start gap-2 rounded-xl border border-[#982598]/15 bg-[#f5eeff] px-4 py-3'>
        <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#982598' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' style={{marginTop:1,flexShrink:0}}>
          <circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/>
        </svg>
        <p className='text-[12px] font-medium text-[#982598]'>
          <strong>Sheet 1:</strong> Covered area per floor — enters per marla (1–80). &nbsp;
          <strong>Sheet 2:</strong> Rate per sq ft — single value each, applies to all marla sizes. &nbsp;
          <strong>Tables:</strong> Select a marla below to preview the 20 generated cost tables.
        </p>
      </div>

      {/* ── Input Sheet 1 */}
      <Sheet1Grid data={sheet1} onChange={handleSheet1Change} />

      {/* ── Input Sheet 2 */}
      <Sheet2Form data={sheet2} onChange={handleSheet2Change} />

      {/* ── Divider: marla selector for preview */}
      <div className='flex flex-wrap items-center gap-3 pt-1'>
        <div className='h-px flex-1 bg-[#f0e4f0] min-w-[30px]' />
        <div className='flex items-center gap-2 rounded-full border border-[#982598]/25 bg-[#f5eeff] px-4 py-2'>
          <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#982598' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
            <polyline points='22 12 18 12 15 21 9 3 6 12 2 12'/>
          </svg>
          <span className='text-[11px] font-semibold text-[#982598]'>Preview 20 Tables for Marla:</span>
          <select
            value={previewMarla}
            onChange={(e) => setPreviewMarla(e.target.value)}
            style={{ border:'none',background:'transparent',fontSize:12,fontWeight:700,color:'#15173d',outline:'none',cursor:'pointer' }}
          >
            {MARLA_COLS.map(({ key, label }) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className='h-px flex-1 bg-[#f0e4f0] min-w-[30px]' />
      </div>

      {/* ── Reference Area Summary Table */}
      <ReferenceTable marlaKey={previewMarla} sheet1={sheet1} />

      {/* ── 20 Generated Tables */}
      <div className='space-y-2.5'>
        {TABLE_CONFIGS.map(config => (
          <GeneratedTable
            key={config.id}
            config={config}
            marlaKey={previewMarla}
            sheet1={sheet1}
            sheet2={sheet2}
          />
        ))}
      </div>
    </form>
  );
};

export default CostOfConstruction;